/**
 * RLS-guarded client reads: the dashboard reading the database's region directly.
 *
 * ## Why this module exists next to `client.ts`
 *
 * `client.ts` is the public API over the api Worker, and every write still goes through it. This
 * is the ONE sanctioned exception `api-platform.md` §16 has always carried — "RLS-guarded client
 * reads … browser-originated, read-only" — switched on for the reason ADR 0049 records with
 * numbers: the Worker is placed in Amsterdam for a request from Muscat, the database is in Mumbai,
 * and a read of the organization card cost P50 2407 ms from the founder's chair. The same read
 * through PostgREST in Mumbai, from the same chair, on the same session: 285 ms. The tiles' query
 * itself executes in 0.99 ms; everything else was the detour.
 *
 * ## What keeps this from being a side door
 *
 * Nothing here is trusted. Every relation this file names has RLS enabled and FORCED; the token is
 * the user's own; `app_private.current_org_id()` reads the `org_id` claim the access-token hook
 * stamped after re-validating membership; and the view carries the same "your own envelopes unless
 * you hold `envelopes.read_all_org`" rule the Worker applied in its handler, evaluated by Postgres.
 * Probed on the live cell before this shipped: a forged `org_id` claim for a real org the user is
 * not a member of sees zero of everything.
 *
 * Every relation read here is declared in `infra/client-reads.json`, and `pnpm lint:client-reads`
 * fails the build on a `.from()` that is not, or on any `.insert/.update/.upsert/.delete` from the
 * browser at all. The registry is what makes "read-only, registered surfaces only" a property of
 * the build rather than a sentence in a document.
 *
 * ## Validated at the boundary, by hand
 *
 * PostgREST returns whatever the view returns. A column renamed by a migration ahead of this build
 * would arrive as `undefined` and render as a blank — or, for a count, as `NaN` folded into a tile.
 * So every row is checked: the expected keys, of the expected types, or it throws where a screen
 * can say so. Same posture `client.ts` takes toward the Worker (CLAUDE.md §4).
 *
 * The strictness is not uniform, and the line is who chooses the shape. A `.from(…).select(…)`
 * read names its own columns, so an EXTRA one means the relation moved underneath the bundle —
 * that is drift, `row` refuses it, and it should be loud. An RPC has no select list: `my_inbox`
 * returns whatever the function returns, so its column set belongs to the migration stream, which
 * S27 guarantees moves ahead of the console. `rpcRow` therefore requires what it needs and ignores
 * what it does not know. Both still refuse a MISSING column, which is the half that would render a
 * blank.
 *
 * These checks are hand-written rather than zod, and that was measured rather than assumed. zod is
 * ~13 KB gzipped and the dashboard is the FIRST PAINT, so it lands in the shared entry chunk that
 * `infra/verify-bundle-budget.mjs` charges to every surface: adding it took the field editor from
 * 21% headroom to 12% and the dashboard from 91.8 KB to 117.1 KB, to validate two shapes with nine
 * fields between them. The editor's budget exists as a tripwire for the fill engine leaking into
 * the browser (ADR 0008 §7), and spending nine points of it here would have made that tripwire
 * worse at its actual job. Forty lines is the cheaper answer.
 */
import { bucketEnvelopeCounts, isReceivedBucket } from '@flowza/core/envelopes/inbox'
import { getClient } from '../supabase'
import type {
  EnvelopeSummaryResource,
  OrganizationCardResource,
  OrganizationResource,
  ReceivedEnvelope,
} from './types'

export class ReadError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ReadError'
  }
}

/** True when this build can reach the database's Data API: a Supabase URL and publishable key. */
export function readsConfigured(): boolean {
  return getClient() !== null
}

function client() {
  const c = getClient()
  if (c === null)
    throw new ReadError('not_configured', 'this deployment has no Supabase configured')
  return c
}

/**
 * A row with EXACTLY these keys, or a throw.
 *
 * Strict in both directions on purpose. A missing key means the read asked for a column that no
 * longer exists; an extra one means the shape grew underneath a bundle that will not render it.
 * Both are the database and this build disagreeing, and both should be loud once rather than blank
 * forever.
 */
function row(value: unknown, keys: readonly string[], what: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new ReadError('unexpected_shape', `${what} did not come back as a row`)
  }
  const record = value as Record<string, unknown>
  const actual = Object.keys(record).sort().join(',')
  const expected = [...keys].sort().join(',')
  if (actual !== expected) {
    throw new ReadError(
      'unexpected_shape',
      `${what} came back with different columns than this build knows (${actual || 'none'})`,
    )
  }
  return record
}

/**
 * The same check for a row whose SHAPE IS NOT THIS BUILD'S TO CHOOSE.
 *
 * `row` above is strict in both directions and should stay that way: every caller of it names its
 * columns in a `.select(...)`, so the bundle decides what comes back and an extra key really does
 * mean the table moved underneath it. That is drift, and drift should be loud.
 *
 * An RPC is the other case. `my_inbox` returns whatever the FUNCTION returns; there is no select
 * list to narrow it, so the column set belongs to the migration stream. S27 is explicit that the
 * Workers and the database never deploy atomically, and ADR 0113 makes the console LAG the API on
 * purpose — so "the database is one column ahead of this bundle" is an ordinary state between a
 * merge and a promotion rather than an anomaly.
 *
 * Measured on production 2026-09-15, which is why this exists: `20260915009900` added
 * `current_revision_number` and `re_signing` to `my_inbox`; the migration applied and the console
 * release did not, because the health gate had failed on an unrelated Supabase maintenance window.
 * Every inbox row then threw and a signed-in person saw no inbox at all. Any additive migration
 * would have done the same to whichever screen read through an RPC.
 *
 * So a MISSING key is still fatal — this build cannot render a row it asked for, and drawing a
 * blank one silently is the worse failure of the two — and an unknown key is ignored, because
 * nothing reads it and nothing can therefore be wrong about it.
 */
function rpcRow(value: unknown, keys: readonly string[], what: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new ReadError('unexpected_shape', `${what} did not come back as a row`)
  }
  const record = value as Record<string, unknown>
  const missing = keys.filter((key) => !Object.prototype.hasOwnProperty.call(record, key))
  if (missing.length > 0) {
    throw new ReadError(
      'unexpected_shape',
      `${what} came back without ${missing.join(', ')} — this build needs ${
        missing.length === 1 ? 'that column' : 'those columns'
      }`,
    )
  }
  return record
}

function str(record: Record<string, unknown>, key: string, what: string): string {
  const value = record[key]
  if (typeof value !== 'string') {
    throw new ReadError('unexpected_shape', `${what}.${key} is not text`)
  }
  return value
}

const ORG_TYPES = ['personal', 'team', 'isv_parent', 'isv_child'] as const
const ORG_COLUMNS = ['id', 'name', 'slug', 'type', 'region', 'created_at'] as const

function organization(value: unknown): OrganizationCardResource {
  const r = row(value, ORG_COLUMNS, 'your organization')
  const type = str(r, 'type', 'your organization')
  if (!(ORG_TYPES as readonly string[]).includes(type)) {
    // Not a label this build has wording for. Rendering the raw value in a Badge would put a
    // database enum in front of a customer.
    throw new ReadError('unexpected_shape', `unknown organization type "${type}"`)
  }
  return {
    id: str(r, 'id', 'your organization'),
    name: str(r, 'name', 'your organization'),
    slug: str(r, 'slug', 'your organization'),
    type: type as OrganizationResource['type'],
    region: str(r, 'region', 'your organization'),
    created_at: str(r, 'created_at', 'your organization'),
  }
}

/**
 * The caller's own organization — `GET /v1/organization`, read from the row.
 *
 * Filtered by the id the token carries rather than "whatever RLS lets me see": a user can be an
 * active member of several organizations, RLS admits every one they belong to, and the console is
 * for the one they are signed into. The Worker's `currentOrganization` made the same choice.
 */
export async function readOrganization(orgId: string): Promise<OrganizationCardResource> {
  const { data, error } = await client()
    .from('organizations')
    .select('id,name,slug,type,region,created_at')
    .eq('id', orgId)
    .is('deleted_at', null)
    .maybeSingle()
  if (error) throw new ReadError(error.code ?? 'postgrest', error.message)
  if (data === null) throw new ReadError('not_found', 'your organization could not be found')
  return organization(data)
}

const COUNT_COLUMNS = ['org_id', 'status', 'count'] as const

function statusCounts(value: unknown): Array<{ status: string; count: number }> {
  if (!Array.isArray(value)) {
    throw new ReadError('unexpected_shape', 'the envelope counts did not come back as a list')
  }
  return value.map((entry) => {
    const r = row(entry, COUNT_COLUMNS, 'an envelope count')
    const count = r['count']
    // The view casts to int for this reason: a bigint would arrive as a string, and `'5'` folded
    // into a tile through arithmetic is a wrong number rather than a missing one.
    if (typeof count !== 'number' || !Number.isInteger(count) || count < 0) {
      throw new ReadError('unexpected_shape', 'an envelope count is not a whole number')
    }
    return { status: str(r, 'status', 'an envelope count'), count }
  })
}

/** One row of `v_envelope_status_counts`: a status this cell recognises, and how many are in it. */
export type StatusCount = { readonly status: string; readonly count: number }

/**
 * What the dashboard gets from one read of the status view: the four buckets AND the rows they were
 * folded from.
 *
 * `byStatus` is not a second request and not a second source — it is the rows `bucketEnvelopeCounts`
 * already consumed, handed to the caller instead of dropped on the floor. Returning both is the
 * point: "Needs your action: 4" cannot tell an operator whether four counterparties refused or four
 * envelopes timed out, which are opposite problems with opposite responses, and the database
 * measured the difference before the fold threw it away.
 */
export type EnvelopeSummary = {
  readonly counts: EnvelopeSummaryResource
  readonly byStatus: readonly StatusCount[]
}

/**
 * The dashboard's pipeline — `v_envelope_status_counts`, read under RLS in the database's region.
 *
 * The view returns a faithful count per status over the envelopes this user may count; WHICH
 * statuses mean "you need to do something" stays in `@flowza/core`'s `bucketEnvelopeCounts`, stated
 * once, exactly where the Worker read it from. Moving the bucketing into SQL would have been a
 * second copy of a product judgement, and the one that drifts is always the one nobody tests.
 */
export async function readEnvelopeSummary(): Promise<EnvelopeSummary> {
  const { data, error } = await client()
    .from('v_envelope_status_counts')
    .select('org_id,status,count')
  if (error) throw new ReadError(error.code ?? 'postgrest', error.message)
  const rows = statusCounts(data)
  return { counts: bucketEnvelopeCounts(rows), byStatus: rows }
}

// ---------------------------------------------------------------------------------------------

const INBOX_COLUMNS = [
  'envelope_id',
  'envelope_signer_id',
  'subject',
  'envelope_status',
  'signer_status',
  'signer_kind',
  'routing_order',
  'sender_org',
  'bucket',
  'my_turn',
  'sent_at',
  'expires_at',
  'completed_at',
  'current_revision_number',
  're_signing',
] as const

function nullableStr(record: Record<string, unknown>, key: string, what: string): string | null {
  const value = record[key]
  if (value === null) return null
  if (typeof value !== 'string')
    throw new ReadError('unexpected_shape', `${what}.${key} is not text`)
  return value
}

function receivedItem(value: unknown): ReceivedEnvelope {
  // rpcRow, not row: the inbox comes back from a FUNCTION, so its column set belongs to the
  // migration stream rather than to this bundle. See rpcRow for what that cost once.
  const r = rpcRow(value, INBOX_COLUMNS, 'an item in your inbox')
  const bucket = str(r, 'bucket', 'an item in your inbox')
  if (!isReceivedBucket(bucket)) {
    // A bucket this build has no tab for. Rendering it would put the row nowhere a person can see
    // it; throwing says so once, where the screen can show it.
    throw new ReadError('unexpected_shape', `unknown inbox bucket "${bucket}"`)
  }
  const order = r['routing_order']
  if (typeof order !== 'number' || !Number.isInteger(order)) {
    throw new ReadError('unexpected_shape', 'an inbox item has no whole routing order')
  }
  const myTurn = r['my_turn']
  if (typeof myTurn !== 'boolean') {
    throw new ReadError('unexpected_shape', 'an inbox item does not say whether it is your turn')
  }
  return {
    envelope_id: str(r, 'envelope_id', 'an item in your inbox'),
    envelope_signer_id: str(r, 'envelope_signer_id', 'an item in your inbox'),
    subject: str(r, 'subject', 'an item in your inbox'),
    envelope_status: str(r, 'envelope_status', 'an item in your inbox'),
    signer_status: str(r, 'signer_status', 'an item in your inbox'),
    signer_kind: str(r, 'signer_kind', 'an item in your inbox'),
    routing_order: order,
    sender_org: str(r, 'sender_org', 'an item in your inbox'),
    bucket,
    my_turn: myTurn,
    sent_at: nullableStr(r, 'sent_at', 'an item in your inbox'),
    expires_at: nullableStr(r, 'expires_at', 'an item in your inbox'),
    completed_at: nullableStr(r, 'completed_at', 'an item in your inbox'),
    // Tolerated rather than demanded, and only these two: a console released ahead of the
    // promotion that adds them would otherwise throw on every row and show an empty inbox.
    // S27 says the two never deploy together, and an inbox that renders without a badge is a
    // far better failure than an inbox that renders nothing.
    current_revision_number:
      typeof r['current_revision_number'] === 'number' ? r['current_revision_number'] : 1,
    re_signing: r['re_signing'] === true,
  }
}

/**
 * What was sent TO the signed-in person — `public.my_inbox()`, the one RPC here that reads.
 *
 * There is no Worker route this replaces: until P7-08 there was no way at all for somebody with an
 * account to see that a document was waiting on them, and the only door to a ceremony was the
 * sealed token in an invitation email.
 *
 * It is an RPC rather than a `.from()` because the answer deliberately CROSSES organizations. RLS
 * on `envelopes` answers "which org are you in", and a recipient usually is not a member of the
 * sending org at all — so a view would hide from a person the very documents they were sent. The
 * predicate that replaces the org check is the caller's own confirmed account email, evaluated in
 * `app_private.inbox_for_me` where the browser cannot reach it.
 */
export async function readMyInbox(): Promise<readonly ReceivedEnvelope[]> {
  const { data, error } = await client().rpc('my_inbox')
  if (error) throw new ReadError(error.code ?? 'postgrest', error.message)
  if (!Array.isArray(data)) {
    throw new ReadError('unexpected_shape', 'your inbox did not come back as a list')
  }
  return data.map(receivedItem)
}

// ---------------------------------------------------------------------------------------------

/** One document's fate in the correction the sender is preparing. */
export type RevisionPreviewDocument = {
  readonly document_id: string
  readonly title: string | null
  readonly position: number | null
  readonly change: 'unchanged' | 'changed' | 'added' | 'removed' | 'not_ready'
}

/** One participant, and what issuing would do to them. */
export type RevisionPreviewSigner = {
  readonly envelope_signer_id: string
  readonly name: string
  readonly kind: string
  readonly routing_order: number
  readonly status: string
  /** re_sign | sign_addendum | re_invite | unchanged | pending | none */
  readonly consequence: string
  readonly signatures: readonly { readonly document_id: string; readonly outcome: string }[]
}

export type RevisionPreview = {
  readonly fromRevision: number
  readonly toRevision: number
  /** content_change | unsigned_field_change | contact_fix — describes; the hashes decided. */
  readonly correctionClass: string
  /** False while a document is still converting (or none exists): the issue would be refused. */
  readonly ready: boolean
  readonly documents: readonly RevisionPreviewDocument[]
  readonly signers: readonly RevisionPreviewSigner[]
}

/**
 * Whether the pause can be closed WITHOUT issuing a revision, and which guard says otherwise.
 *
 * `blocker` is null exactly when `eligible` is true. The screen shows the reason rather than just
 * hiding the choice: a sender who edited a document and then found no quiet option would go
 * looking for a bug, where "a document changed, so this has to be issued as a revision" is the
 * answer and the remedy at once.
 */
export type QuietEditPreview = {
  readonly eligible: boolean
  /**
   * not_correcting | not_your_pause | request_open | revision_not_frozen | document_not_ready |
   * documents_changed | already_signed. Null when eligible.
   */
  readonly blocker: string | null
  /** The revision the envelope is on, and stays on: a quiet edit issues none. */
  readonly revisionNumber: number
  readonly signaturesStanding: number
  readonly fieldsAdded: number
  readonly fieldsRemoved: number
  /** Answers a field removed during the pause is holding, which closing would discard. */
  readonly answersDiscarded: number
}

/**
 * `public.quiet_edit_preview` — may this pause end without a revision? (ADR 0163)
 *
 * Read alongside `previewRevision` while the envelope is paused. The two answer different
 * questions about the same moment — that one is "what would issuing do", this one is "does issuing
 * have to happen at all" — and the sender needs both to choose.
 */
export async function quietEditPreview(envelopeId: string): Promise<QuietEditPreview> {
  const { data, error } = await client().rpc('quiet_edit_preview', {
    p_envelope_id: envelopeId.replace(/^env_/, ''),
  })
  if (error) throw new ReadError(error.code ?? 'postgrest', error.message)
  if (data === null || typeof data !== 'object') {
    throw new ReadError('unexpected_shape', 'the quiet-edit preview did not come back as an object')
  }
  const p = data as Record<string, unknown>
  const num = (key: string): number => (typeof p[key] === 'number' ? (p[key] as number) : 0)
  return {
    eligible: p['eligible'] === true,
    blocker: typeof p['blocker'] === 'string' ? p['blocker'] : null,
    revisionNumber: num('revision_number'),
    signaturesStanding: num('signatures_standing'),
    fieldsAdded: num('fields_added'),
    fieldsRemoved: num('fields_removed'),
    answersDiscarded: num('answers_discarded'),
  }
}

const PREVIEW_CHANGES = new Set(['unchanged', 'changed', 'added', 'removed', 'not_ready'])

/**
 * `public.preview_revision` — what issuing the correction would do, computed from the hashes.
 *
 * Read before the sender confirms (ux.md §5): every document's change and every participant's
 * consequence. A refusal (PT409) means the envelope is not under correction.
 */
export async function previewRevision(envelopeId: string): Promise<RevisionPreview> {
  const { data, error } = await client().rpc('preview_revision', {
    p_envelope_id: envelopeId.replace(/^env_/, ''),
  })
  if (error) throw new ReadError(error.code ?? 'postgrest', error.message)
  if (data === null || typeof data !== 'object') {
    throw new ReadError('unexpected_shape', 'the preview did not come back as an object')
  }
  const p = data as Record<string, unknown>
  const num = (v: unknown, fallback: number): number => (typeof v === 'number' ? v : fallback)
  const docs = Array.isArray(p['documents']) ? (p['documents'] as unknown[]) : []
  const signers = Array.isArray(p['signers']) ? (p['signers'] as unknown[]) : []
  return {
    fromRevision: num(p['from_revision'], 1),
    toRevision: num(p['to_revision'], 2),
    correctionClass:
      typeof p['correction_class'] === 'string' ? p['correction_class'] : 'contact_fix',
    ready: p['ready'] === true,
    documents: docs.flatMap((d) => {
      const doc = d as Record<string, unknown>
      const change = doc['change']
      if (
        typeof doc['document_id'] !== 'string' ||
        typeof change !== 'string' ||
        !PREVIEW_CHANGES.has(change)
      )
        return []
      return [
        {
          document_id: doc['document_id'],
          title: typeof doc['title'] === 'string' ? doc['title'] : null,
          position: typeof doc['position'] === 'number' ? doc['position'] : null,
          change: change as RevisionPreviewDocument['change'],
        },
      ]
    }),
    signers: signers.flatMap((s) => {
      const row = s as Record<string, unknown>
      if (typeof row['envelope_signer_id'] !== 'string') return []
      const sigs = Array.isArray(row['signatures']) ? (row['signatures'] as unknown[]) : []
      return [
        {
          envelope_signer_id: row['envelope_signer_id'],
          name: typeof row['name'] === 'string' ? row['name'] : '',
          kind: typeof row['kind'] === 'string' ? row['kind'] : 'signer',
          routing_order: num(row['routing_order'], 1),
          status: typeof row['status'] === 'string' ? row['status'] : '',
          consequence: typeof row['consequence'] === 'string' ? row['consequence'] : 'pending',
          signatures: sigs.flatMap((x) => {
            const sig = x as Record<string, unknown>
            return typeof sig['document_id'] === 'string' && typeof sig['outcome'] === 'string'
              ? [{ document_id: sig['document_id'], outcome: sig['outcome'] }]
              : []
          }),
        },
      ]
    }),
  }
}

// ---------------------------------------------------------------------------------------------

const CONTACT_COLUMNS = [
  'id',
  'org_id',
  'name',
  'email',
  'company',
  'job_title',
  'default_locale',
  'notes',
  'created_at',
  'updated_at',
  'envelope_count',
] as const

export type ContactRow = {
  readonly id: string
  readonly name: string
  readonly email: string
  readonly company: string | null
  readonly job_title: string | null
  readonly default_locale: string | null
  readonly notes: string | null
  readonly updated_at: string
  /** How many envelopes this address has been on. Derived by the view, never stored. */
  readonly envelope_count: number
}

function contact(value: unknown): ContactRow {
  const r = row(value, CONTACT_COLUMNS, 'a contact')
  const count = r['envelope_count']
  // The view casts to int for this reason: a bigint arrives as a STRING through PostgREST, and a
  // string rendered as a count is a wrong number rather than a missing one.
  if (typeof count !== 'number' || !Number.isInteger(count) || count < 0) {
    throw new ReadError('unexpected_shape', 'a contact has no whole envelope count')
  }
  return {
    id: str(r, 'id', 'a contact'),
    name: str(r, 'name', 'a contact'),
    email: str(r, 'email', 'a contact'),
    company: nullableStr(r, 'company', 'a contact'),
    job_title: nullableStr(r, 'job_title', 'a contact'),
    default_locale: nullableStr(r, 'default_locale', 'a contact'),
    notes: nullableStr(r, 'notes', 'a contact'),
    updated_at: str(r, 'updated_at', 'a contact'),
    envelope_count: count,
  }
}

/**
 * The organization's address book, with each contact's signing history.
 *
 * Read straight from the database's region rather than through the Worker, for the reason ADR 0049
 * measured: `contacts` is RLS'd to the caller's org, so the query is safe by construction and the
 * Amsterdam detour buys nothing. Ordered by name in SQL rather than here, so paging later does not
 * change what "first" means.
 */
export async function readContacts(): Promise<readonly ContactRow[]> {
  const { data, error } = await client()
    .from('v_contacts_with_history')
    .select(CONTACT_COLUMNS.join(','))
    .order('name', { ascending: true })
  if (error) throw new ReadError(error.code ?? 'postgrest', error.message)
  if (!Array.isArray(data)) {
    throw new ReadError('unexpected_shape', 'your contacts did not come back as a list')
  }
  return data.map(contact)
}
